import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantitySelector from "../components/QuantitySelector";
import { Comments, Details, ShippingInfo } from "../components/Products";
import { addToCart } from "../redux/store/cartSlice";
import NewsTicker from "../components/Home/Newtricker";
import { toast } from "react-toastify";
import CartDrawer from "../components/CartDrawer";
import ProductCard from "../components/Home/ProductCard";
import { FaAngleLeft, FaAngleRight, FaHeart } from "react-icons/fa";
import { addToWishlist } from "../redux/store/wishlist";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cartDrawer, setCartDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [product, setProduct] = useState(null);
  // ------------------ Redux ------------------
  const { allProducts = [], loading } = useSelector((state) => state.products);
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [animating, setAnimating] = useState(false);
  const isInWishlist = wishlistItems?.some((item) => item.id === product?.id);

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 2000);
  };

  const handleWishlist = () => {
    if (!product) return;

    setAnimating(true);
    setTimeout(() => setAnimating(false), 400);

    if (isInWishlist) {
      showAlert("Already in wishlist", "remove");
    } else {
      dispatch(addToWishlist(product));
      showAlert("Added to wishlist", "add");
    }
  };
  // ------------------ Local State ------------------

  const sliderRef = useRef(null);

  // ------------------ Slider ------------------
  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      const maxScroll =
        sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
      if (sliderRef.current.scrollLeft >= maxScroll - 10) {
        sliderRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        sliderRef.current.scrollBy({ left: 300, behavior: "smooth" });
      }
    }
  };

  // ------------------ Find Product ------------------
  useEffect(() => {
    if (!allProducts.length) return;
    const found = allProducts.find((p) => String(p.id) === String(id));
    if (found) {
      setProduct(found);
    } else {
      setProduct(undefined); // explicitly not found
    }
  }, [allProducts, id]);

  // ------------------ Set Variant & Image on Load ------------------
  useEffect(() => {
    if (!product) return;

    setQuantity(1);

    if (product.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      setSelectedVariant(firstVariant);
      setActiveVariantId(firstVariant.id);
      const variantImage = product.images?.find(
        (img) => img.id === firstVariant.imageId,
      );
      setActiveImage(variantImage?.src || product.image || null);
    } else {
      setSelectedVariant(null);
      setActiveVariantId(null);
      setActiveImage(product.images?.[0]?.src || product.image || null);
    }
  }, [product]);

  // ------------------ Reset quantity on variant change ------------------
  useEffect(() => {
    setQuantity(1);
  }, [selectedVariant]);

  // ------------------ Early Returns ------------------
  if (loading) {
    return (
      <div className="text-center py-20 text-gray-500 text-lg">Loading...</div>
    );
  }

  if (!loading && allProducts.length > 0 && product === undefined) {
    return (
      <div className="text-center py-20 text-gray-500 text-lg">
        Product Not Found
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-20 text-gray-500 text-lg">Loading...</div>
    );
  }

  // ------------------ Related Products ------------------
  const relatedProducts = allProducts.filter((p) => {
    if (p.id === product.id) return false;
    const sameVendor = p.vendor === product.vendor;
    const productTags = Array.isArray(product.tags) ? product.tags : [];
    const pTags = Array.isArray(p.tags) ? p.tags : [];
    const commonTags = pTags.some((tag) => productTags.includes(tag));
    return sameVendor || commonTags;
  });

  // ------------------ Handlers ------------------
  const buildCartProduct = () => ({
    ...product,
    image: activeImage || product.image,
    selectedVariant,
    quantity,
  });

  const isSoldOut =
    selectedVariant && Number(selectedVariant.inventory_quantity || 0) <= 0;

  const handleAddtoCart = () => {
    if (isSoldOut) {
      toast.error("Selected variant is sold out");
      return;
    }

    dispatch(addToCart(buildCartProduct()));
    toast.success("Added to Cart Successfully");
    setCartDrawer(true);
  };

  const handleBuyNow = () => {
    if (isSoldOut) {
      toast.error("Selected variant is sold out");
      return;
    }

    dispatch(addToCart(buildCartProduct()));
    navigate("/checkout");
  };

  const displayPrice = Number(
    selectedVariant?.price || product.variants?.[0]?.price || 0,
  );
  const compareAtPrice = selectedVariant?.compare_at_price;
  const stockCount = Number(selectedVariant?.inventory_quantity || 0);

  // ------------------ JSX ------------------
  return (
    <>
      <Navbar />
      <div className="bg-white px-[1rem] py-[1rem] lg:px-[6rem]">
        {/* Breadcrumb */}
        <div className="mb-[2rem] w-full bg-white py-[1rem] text-[15px] text-gray-500">
          <span className="text-gray-800">
            <Link to="/">Home</Link>
          </span>{" "}
          {">"} {product.title}
        </div>

        <div className="mb-[3rem] grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(380px,0.95fr)] lg:gap-12">
          {/* LEFT - IMAGE */}
          <div>
            <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-gray-200 bg-[#f7f7f5] p-6 md:min-h-[560px]">
              {activeImage && (
                <img
                  src={activeImage}
                  alt={product.title}
                  className="h-full max-h-[500px] w-full max-w-[560px] object-contain"
                />
              )}
            </div>
            <div className="mt-4 grid grid-flow-col auto-cols-[82px] gap-3 overflow-x-auto pb-2">
              {product.images?.map((img) => (
                <button
                  key={img.id}
                  onClick={() => setActiveImage(img.src)}
                  className={`h-20 w-20 rounded-md border bg-white p-1 transition ${
                    activeImage === img.src
                      ? "border-black"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={product.title}
                    className="h-full w-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT - DETAILS */}
          <div className="flex flex-col gap-6 text-gray-800">
            <div>
              <p className="mb-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                {product.vendor}
              </p>
              <h1 className="font-Syne text-[30px] font-bold leading-tight text-gray-950 lg:text-[42px]">
                {product.title}
              </h1>
            </div>

            {/* Stock Info */}
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-Syne text-[28px] font-semibold text-gray-950">
                ${displayPrice.toFixed(2)}
              </p>
              {compareAtPrice && (
                <p className="text-[18px] font-semibold text-gray-400 line-through">
                  ${Number(compareAtPrice).toFixed(2)}
                </p>
              )}
              {selectedVariant && (
                <span
                  className={`rounded-full px-3 py-1 text-[13px] font-semibold ${
                    isSoldOut
                      ? "bg-red-50 text-red-600"
                      : "bg-green-50 text-green-700"
                  }`}
                >
                  {isSoldOut ? "Sold out" : `${stockCount} in stock`}
                </span>
              )}
            </div>

            {/* Product Meta */}
            <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 text-[15px] md:grid-cols-2">
              {product.product_type && (
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Type
                  </p>
                  <p className="mt-1 text-gray-800">{product.product_type}</p>
                </div>
              )}
              {product.vendor && (
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Brand
                  </p>
                  <p className="mt-1 text-gray-800">{product.vendor}</p>
                </div>
              )}
              {Array.isArray(product.tags) && product.tags.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Tags
                  </p>
                  <p className="mt-1 text-gray-700">
                    {product.tags.slice(0, 8).join(", ")}
                  </p>
                </div>
              )}
            </div>

            {/* Variants */}
            {product.variants?.length > 1 && (
              <div>
                <div className="mb-3 flex items-end justify-between gap-4">
                  <h2 className="font-Syne text-[18px] font-semibold text-gray-950">
                    Select option
                  </h2>
                  {selectedVariant?.title &&
                    selectedVariant.title !== "Default Title" && (
                      <p className="text-[13px] text-gray-500">
                        Selected: {selectedVariant.title}
                      </p>
                    )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                {product.variants.map((variant) => {
                  const variantImage = product.images?.find(
                    (img) => img.id === variant.imageId,
                  );
                  const variantSoldOut =
                    Number(variant.inventory_quantity || 0) <= 0;
                  return (
                    <button
                      type="button"
                      key={variant.id}
                      onClick={() => {
                        setActiveVariantId(variant.id);
                        setSelectedVariant(variant);
                        setActiveImage(variantImage?.src || product.image);
                      }}
                      className={`flex min-h-[92px] cursor-pointer items-center gap-3 rounded-lg border p-3 text-left transition ${
                        activeVariantId === variant.id
                          ? "border-gray-950 bg-gray-950 text-white"
                          : "border-gray-200 bg-white text-gray-800 hover:border-gray-500"
                      }`}
                    >
                      {variantImage && (
                        <img
                          src={variantImage.src}
                          alt={variant.title}
                          className="h-[58px] w-[58px] rounded-md bg-white object-contain p-1"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-semibold">
                          {!variant.title || variant.title === "Default Title"
                            ? "Default"
                            : variant.title}
                        </h3>
                        <p className="mt-1 font-semibold">
                          ${Number(variant.price).toFixed(2)}
                        </p>
                        <p
                          className={`mt-1 text-[12px] ${
                            activeVariantId === variant.id
                              ? "text-gray-200"
                              : variantSoldOut
                                ? "text-red-500"
                                : "text-gray-500"
                          }`}
                        >
                          {variantSoldOut
                            ? "Sold out"
                            : `${variant.inventory_quantity} available`}
                        </p>
                      </div>
                    </button>
                  );
                })}
                </div>
              </div>
            )}

            {/* Price + Quantity */}
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-[14px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                Quantity
              </p>
              <QuantitySelector
                value={quantity}
                max={selectedVariant?.inventory_quantity || 99}
                min={1}
                onChange={(newQty) => setQuantity(newQty)}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={handleAddtoCart}
                disabled={isSoldOut}
                className="h-12 flex-1 rounded-md bg-gray-950 px-6 text-[16px] font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Add to cart
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={isSoldOut}
                className="h-12 flex-1 rounded-md border border-gray-950 bg-white px-6 text-[16px] font-semibold text-gray-950 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
              >
                Buy now
              </button>
              <div className="relative">
                {/* Alert */}
                {alert.show && (
                  <div
                    className={`
            absolute -top-10 left-1/2 -translate-x-1/2
            whitespace-nowrap
            text-xs font-medium
            px-3 py-1.5
            rounded-full
            shadow-md
            transition-all duration-300
            z-50
            ${
              alert.type === "add"
                ? "bg-red-500 text-white"
                : "bg-gray-700 text-white"
            }
          `}
                  >
                    {alert.message}
                  </div>
                )}

                {/* Button */}
                <button
                  onClick={handleWishlist}
                  className={`
          relative
          w-11 h-11
          flex items-center justify-center
          rounded-full
          border
          shadow-sm
          transition-all duration-300 ease-out
          hover:-translate-y-0.5 hover:shadow-md
          active:scale-95
          focus:outline-none focus:ring-2 focus:ring-red-200
          ${isInWishlist ? "bg-red-50 border-red-400" : "bg-white border-gray-200"}
          ${animating ? "scale-125" : "scale-100"}
        `}
                >
                  <FaHeart
                    size={18}
                    className={`
            transition-all duration-300
            ${isInWishlist ? "text-red-500" : "text-red-300"}
          `}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div>
          <div className="flex gap-[1rem] items-center">
            {[
              { id: 1, label: "Details" },
              { id: 2, label: "Shipping Info" },
              { id: 3, label: "Reviews" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-2 text-[1rem] font-semibold cursor-pointer ${
                  activeTab === tab.id
                    ? "border-b-2 border-[#FFDD6A] text-gray-800"
                    : "text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <hr className="mb-[2rem]" />
          <div className="text-gray-700">
            {activeTab === 1 && <Details data={product} />}
            {activeTab === 2 && <ShippingInfo />}
            {activeTab === 3 && (
              <Comments title={product.title} products={product} />
            )}
          </div>
        </div>
      </div>

      <NewsTicker />

      {/* Related Products — only show if there are any */}
      {relatedProducts.length > 0 && (
        <div>
          <div className="w-[100%] text-center flex flex-col items-center justify-center gap-2 mt-[2rem]">
            <h1 className="font-syne text-2xl md:text-4xl font-bold text-gradient-black">
              View More Products
            </h1>
          </div>

          <div className="bg-white pt-10 pb-10 overflow-x-hidden relative hide-scrollbar">
            <div className="container mx-auto px-4 flex flex-col items-center gap-4">
              <div className="w-full relative hide-scrollbar px-4">
                <div
                  ref={sliderRef}
                  className="flex gap-4 overflow-x-auto scroll-smooth px-2 hide-scrollbar"
                  style={{
                    scrollSnapType: "x mandatory",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {relatedProducts.map((item, index) => (
                    <div key={item.id || index} className="flex-shrink-0">
                      <ProductCard products={item} />
                    </div>
                  ))}
                </div>

                {/* Left Button */}
                <button
                  onClick={scrollLeft}
                  className="absolute top-1/2 left-0 sm:left-2 md:left-[-10px] lg:left-[-40px] w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center transform -translate-y-1/2 z-20 shadow-lg hover:scale-110 transition-all"
                >
                  <FaAngleLeft className="text-black text-xl sm:text-2xl" />
                </button>

                {/* Right Button */}
                <button
                  onClick={scrollRight}
                  className="absolute top-1/2 right-0 sm:right-2 md:right-[-10px] lg:right-[-60px] w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center transform -translate-y-1/2 z-20 shadow-lg hover:scale-110 transition-all"
                >
                  <FaAngleRight className="text-black text-xl sm:text-2xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      {cartDrawer && <CartDrawer onClose={() => setCartDrawer(false)} />}
    </>
  );
};

export default ProductDetails;
